import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as analyticsService from "services/analyticsService";
import * as flagsActions from "store/actions/input/flags/flagsActions";
import * as analyticsStore from "store/analytics";
import useDebouncedValue from "store/hooks/useDebouncedValue";
import * as modelDataStore from "store/modelDataStore";
import { selectResolvedLayers } from "store/selectors/pricing/pricingSelectors";
import * as submissionSelector from "store/selectors/submission/submissionSelector";
import * as userSelectors from "store/selectors/user/userSelectors";

const ENGINE = "verve_iab_v1";
const PRICING_KEY = "programPricing";
const CONFIG_KEY = "iabPricingConfig";

export const usePricing = () => {
  const dispatch = useDispatch();
  const isUserReadOnly = useSelector(userSelectors.isUserReadOnly);
  const layers = useSelector(selectResolvedLayers);
  const submission = useDebouncedValue(
    useSelector(submissionSelector.selectResolvedSubmissionWithoutInput)
  );
  const analytics = useSelector(analyticsStore.select);
  const pricing = analytics?.[PRICING_KEY];
  const config = analytics?.[CONFIG_KEY];

  const [triggerAnalytics] = analyticsService.useLazySubmissionAnalyticsQuery();
  const [triggerConfig] = analyticsService.useLazyEngineConfigQuery();

  const [isFetching, setIsFetching] = useState(false);

  const modelData = useSelector(modelDataStore.selectModelDataInput);
  const iabConfig = useMemo(() => modelData?.iabConfig ?? {}, [
    modelData?.iabConfig,
  ]);

  const fetchConfig = useCallback(async () => {
    if (config?.values?.ratingFactors) return;
    const result = await triggerConfig({ engine: ENGINE });
    if (result.data?.values) {
      dispatch(
        analyticsStore.update({ [CONFIG_KEY]: { values: result.data.values } })
      );
    }
  }, [config, triggerConfig, dispatch]);

  const refreshConfig = useCallback(async () => {
    dispatch(analyticsStore.update({ [CONFIG_KEY]: null }));
    const result = await triggerConfig({ engine: ENGINE });
    if (result.data?.values) {
      dispatch(
        analyticsStore.update({ [CONFIG_KEY]: { values: result.data.values } })
      );
      return result.data.values;
    }
    return null;
  }, [dispatch, triggerConfig]);

  const fetchPricing = useCallback(
    async (
      factorSelections = {},
      baseRatingFactors = {},
      displayNameMap = {}
    ) => {
      if (isUserReadOnly) return;
      setIsFetching(true);

      const overrideExposures = Object.entries(factorSelections)
        .filter(([, category]) => category)
        .map(([type, category]) => ({ type, category }));
      const existingExposures = submission?.flags?.exposures ?? [];
      const overrideTypes = new Set(overrideExposures.map((f) => f.type));
      const mergedExposures = [
        ...existingExposures.filter((f) => !overrideTypes.has(f.type)),
        ...overrideExposures,
      ];

      // Merge: last run defaults → current config defaults → per-submission overrides
      const lastRunFactors = pricing?.lastRunConfig?.ratingFactors ?? {};
      const submissionOverrides = iabConfig?.ratingFactors ?? {};
      // Persisted as lastRunConfig so legacy factors survive subsequent recalculations
      const effectiveBaseFactors = { ...lastRunFactors, ...baseRatingFactors };
      const allKeys = new Set([
        ...Object.keys(effectiveBaseFactors),
        ...Object.keys(submissionOverrides),
      ]);
      const mergedRatingFactors = {};
      for (const key of allKeys) {
        mergedRatingFactors[key] = {
          ...(effectiveBaseFactors[key] ?? {}),
          ...(submissionOverrides[key] ?? {}),
        };
      }

      try {
        const baseSubmission = {
          ...submission,
          flags: { ...(submission?.flags ?? {}), exposures: mergedExposures },
          modelData: {
            ...(submission?.modelData ?? {}),
            iabConfig: { ...iabConfig, ratingFactors: mergedRatingFactors },
          },
        };
        const results = await Promise.all(
          layers.map((_, index) =>
            triggerAnalytics({
              engine: ENGINE,
              metric: "layer_pricing",
              submission: {
                ...baseSubmission,
                submission: { state: { pricing: { activeLayer: index } } },
              },
            })
          )
        );
        const layerResults = {};
        results.forEach((r, i) => {
          if (r.data) layerResults[i] = r.data;
        });
        dispatch(
          analyticsStore.update({
            [PRICING_KEY]: {
              layers: layerResults,
              lastRunConfig: {
                ratingFactors: effectiveBaseFactors,
                displayNameMap,
              },
            },
          })
        );
        dispatch(flagsActions.update({ exposures: mergedExposures }));
      } finally {
        setIsFetching(false);
      }
    },
    [
      isUserReadOnly,
      layers,
      triggerAnalytics,
      dispatch,
      submission,
      iabConfig,
      pricing?.lastRunConfig?.ratingFactors,
    ]
  );

  const applyLatestConfig = useCallback(
    (baseRatingFactors, displayNameMap) => {
      dispatch(
        analyticsStore.update({
          [PRICING_KEY]: {
            ...pricing,
            lastRunConfig: { ratingFactors: baseRatingFactors, displayNameMap },
          },
        })
      );
    },
    [dispatch, pricing]
  );

  const updateRatingFactorOverride = useCallback(
    (factorKey, category, value) => {
      const current = modelData?.iabConfig?.ratingFactors ?? {};
      const currentFactor = { ...(current[factorKey] ?? {}) };
      if (value == null) {
        delete currentFactor[category];
      } else {
        currentFactor[category] = value;
      }
      const updatedFactors = { ...current, [factorKey]: currentFactor };
      if (Object.keys(currentFactor).length === 0) {
        delete updatedFactors[factorKey];
      }
      dispatch(
        modelDataStore.update({
          iabConfig: { ...iabConfig, ratingFactors: updatedFactors },
        })
      );
    },
    [dispatch, modelData, iabConfig]
  );

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return {
    data: pricing,
    config: config?.values,
    iabConfig,
    isFetching,
    layers,
    refetch: fetchPricing,
    refreshConfig,
    applyLatestConfig,
    updateRatingFactorOverride,
  };
};
